import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnInit, computed, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AccountBalanceService } from '@frontend/sports/account-balance/feature';
import { QuickBetConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { UserService } from '@frontend/sports/user/feature';
import { UserLoginEvent, UserUpdateEvent } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { EpcotConfigService, EpcotModule } from 'packages/sports/web/app/src/common/epcot-config.service';
import { IncrementalStakesService } from 'packages/sports/web/app/src/numpad/incremental-stakes.service';
import { NumpadSource } from 'packages/sports/web/app/src/numpad/model';
import { NumpadService } from 'packages/sports/web/app/src/numpad/numpad.service';
import { Observable, combineLatest, distinctUntilChanged, filter, first, map, tap } from 'rxjs';

import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipReturnsMessagesAccaBoostService } from '../../../digital/services/betslip-returns-messages-acca-boost.service';
import { BetslipGeneratorContextType } from '../../bet-generation/models';
import { QuickDepositSource } from '../../quick-deposit/quick-deposit.models';
import { IQuickDepositState } from '../../quick-deposit/quick-deposit.state';
import { quickDepositSelector } from '../../quick-deposit/selector';
import { RewardTokenContext } from '../../reward-tokens/reward-tokens.model';
import { QUICK_BET_NUMPAD } from '../../stake/models';
import { BetslipSummaryService } from '../../summary/services/betslip-summary.service';
import BetslipErrorsActions from '../../validation/actions';
import { QuickBetActions } from '../quick-bet-actions';
import {
    quickBetStateSelector,
    selectQuickBetContext,
    selectQuickBetFreebetToken,
    selectQuickBetLockedState,
    selectQuickBetPicks,
} from '../quick-bet.selectors';
import { IQuickBetState } from '../quick-bet.state';
import { QuickBetStakeStatusService } from '../services/quick-bet-stake-status.service';
import { QuickBetUtils } from '../services/quick-bet-utils.service';

export class CollapseAnimationConfig {
    static RewardTokensMaxHeight = 34;
    static FooterMaxHeight = 56;
    static duration = 100;
}

export type RewardTokenAnimationState = 'in' | 'out';

@Component({
    selector: 'bs-quick-bet',
    templateUrl: './quick-bet.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./quick-bet.component.scss'],
    animations: [
        trigger('rewardTokenInOut', [
            state(
                'in',
                style({
                    'max-height': `${CollapseAnimationConfig.RewardTokensMaxHeight}px`,
                    'opacity': 1,
                }),
            ),
            state(
                'out',
                style({
                    'max-height': 0,
                    'opacity': 0,
                }),
            ),
            transition('* => *', animate(`${CollapseAnimationConfig.duration}ms ease-in-out`)),
        ]),
        trigger('footerInOut', [
            state(
                'in',
                style({
                    'max-height': `${CollapseAnimationConfig.FooterMaxHeight}px`,
                    'opacity': 1,
                }),
            ),
            state(
                'out',
                style({
                    'max-height': 0,
                    'opacity': 0,
                }),
            ),
            transition('* => *', animate(`${CollapseAnimationConfig.duration}ms ease-in-out`)),
        ]),
    ],
    providers: [NumpadService],
    standalone: false,
})
export class QuickBetComponent implements OnInit {
    @Input() oddsDisplayFormat: OddsDisplayFormat;

    quickBetState$: Observable<IQuickBetState>;
    quickDepositOpen = false;
    hasNewCustomerOfferPick = false;
    readonly quickDepositSource = QuickDepositSource.QuickBet;
    module = EpcotModule.QuickBet;
    isBetBuilder: boolean;
    isEpcotEnabled = this.epcotConfigService.isEnabled();
    quickDepositState$: Observable<IQuickDepositState>;
    readonly notifications$ = this.returnsMessagesAccaBoostService.accaBoostNotifications$;
    readonly summary$ = this.betslipSummaryService.getSummaryViewModel(BetslipGeneratorContextType.Betslip);
    vm$: Observable<{
        quickBetState: IQuickBetState;
        rewardTokenContext: RewardTokenContext;
        pickList: BetslipPick[];
        isExpanded: boolean;
    }>;

    rewardTokenContext$: Observable<RewardTokenContext>;
    protected QUICK_BET_NUMPAD = QUICK_BET_NUMPAD;
    private keypadOptions = {
        id: QUICK_BET_NUMPAD,
        showConfirm: true,
        showIncrementalStakes: true,
        focusOnLoad: false,
        numpadSource: NumpadSource.QuickBet,
    };

    private quickBetFreebetToken = this.store.selectSignal(selectQuickBetFreebetToken);

    rewardTokensAnimation = signal<RewardTokenAnimationState>('in');
    footerAnimation = computed(() => (this.isEpcotEnabled ? this.rewardTokensAnimation() : 'in'));
    numpadInitialized$ = this.incrementalStakesService.getValues$().pipe(
        first(),
        tap((stakes) => {
            this.numpadService.initialize(null, this.keypadOptions, stakes);
        }),
        map(() => true),
    );

    constructor(
        private accountBalanceService: AccountBalanceService,
        private epcotConfigService: EpcotConfigService,
        public quickBetUtils: QuickBetUtils,
        public sitecore: Sitecore,
        public quickBetConfig: QuickBetConfig,
        public numpadService: NumpadService,
        private m2User: UserService,
        private store: Store,
        private betslipSummaryService: BetslipSummaryService,
        private returnsMessagesAccaBoostService: BetslipReturnsMessagesAccaBoostService,
        private destroyRef: DestroyRef,
        private quickBetStakeStatusService: QuickBetStakeStatusService,
        private incrementalStakesService: IncrementalStakesService,
    ) {}

    isLocked$: Observable<boolean>;

    ngOnInit(): void {
        this.quickBetState$ = this.store.select(quickBetStateSelector);
        this.quickDepositState$ = this.store.select(quickDepositSelector);
        this.isLocked$ = this.store.select(selectQuickBetLockedState);

        this.rewardTokenContext$ = this.store.select(selectQuickBetContext);

        combineLatest([this.numpadService.state$, this.store.select(selectQuickBetFreebetToken)])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([numpadState, isFreeBetToken]) =>
                isFreeBetToken || numpadState.isOpened || !numpadState.stake.isEmpty
                    ? this.rewardTokensAnimation.set('in')
                    : this.rewardTokensAnimation.set('out'),
            );

        this.vm$ = combineLatest([
            this.quickBetState$,
            this.rewardTokenContext$,
            this.store.select(selectQuickBetPicks),
            this.quickBetStakeStatusService.isQuickBetStakeActive$,
        ]).pipe(
            map(([quickBetState, rewardTokenContext, pickList, isExpanded]) => ({
                quickBetState,
                rewardTokenContext,
                pickList,
                isExpanded,
            })),
        );

        this.m2User.events
            .pipe(
                filter((e) => (e instanceof UserUpdateEvent && e.diff.has('balanceProperties')) || e instanceof UserLoginEvent),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((e) => {
                this.validateBetslip();
                if (e instanceof UserLoginEvent) {
                    this.showWalletSelector();
                }
            });

        this.quickDepositState$
            .pipe(
                map((quickDepositState) => quickDepositState.isOpen || quickDepositState.isVisible),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((open) => {
                this.quickDepositOpen = open;
            });

        this.accountBalanceService.selectedWalletType$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.validateBetslip();
        });

        this.store
            .select(selectQuickBetPicks)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((picks) => (this.hasNewCustomerOfferPick = picks.some((pick) => pick.isNewCustomerOfferPick) ?? false));
    }

    showWalletSelector = () => this.store.dispatch(QuickBetActions.showWalletSelector());

    validateBetslip = () => this.store.dispatch(BetslipErrorsActions.validateBetslip());

    toggleQbForm() {
        if (this.quickBetFreebetToken()) {
            return;
        }

        if (this.numpadService.currentState.isOpened) {
            this.numpadService.confirm();
        } else {
            this.numpadService.open();
        }
    }
}
